import {mapMaybes} from '@freckle/maybe'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import type {UnmanagedCourseT} from '@freckle/school/ts/common/models/unmanaged-course'

export type UnmanagedSummaryT = {
  numUnmanagedStudents: number
  schoolIds: Array<number>
  identityProviders: Array<IdentityProviderT>
}

export type CleanUpPhaseT = // Initial state
  // Merging duplicates
  | {
      tag: 'start'
    } // Finished merging - this is an end state when we don't need to archive courses
  | {
      tag: 'merging-duplicates'
    } // Scanning courses
  | {
      tag: 'finished-merging'
      unmanaged: UnmanagedSummaryT
    } // No courses found - this is another end state
  | {
      tag: 'scanning-courses'
      unmanaged: UnmanagedSummaryT
    } // Finished scanning courses
  | {
      tag: 'no-courses-found'
      unmanaged: UnmanagedSummaryT
    } // Kickoff the Archiving courses process
  | {
      tag: 'finished-scanning'
      unmanaged: UnmanagedSummaryT
      remaining: Array<UnmanagedCourseT>
    } // Archiving courses - used to show progress
  | {
      tag: 'start-archiving-courses'
      unmanaged: UnmanagedSummaryT
      archived: Array<UnmanagedCourseT>
      remaining: Array<UnmanagedCourseT>
    } // Just redirect back to Dashboard
  | {
      tag: 'archiving-courses'
      unmanaged: UnmanagedSummaryT
      archived: Array<UnmanagedCourseT>
      remaining: Array<UnmanagedCourseT>
    }
  | {
      tag: 'finished-archiving'
    }
  | {
      tag: 'fetching-duplicates'
    }

export function calculateUnmanagedStudents(
  schools: Array<SchoolAttrs>,
  summary: SummaryT,
): UnmanagedSummaryT {
  const schoolIds = schools.map(school => school.id)
  const activeIdps = new Set(mapMaybes(schools, school => school.activeIdp))
  const {studentCounts} = summary
  const {unknown, manual, idpSync, upload, classlink, clever, renaissance} = studentCounts

  // Defensive case - if this component is visible, there should always
  // be at least one active IDP.
  if (activeIdps.size === 0) {
    return {numUnmanagedStudents: 0, identityProviders: [], schoolIds}
  }

  // The summary counts should not be double-counting
  let numUnmanagedStudents = unknown + manual + idpSync + upload + classlink + clever + renaissance
  if (activeIdps.has('clever')) {
    numUnmanagedStudents -= clever
  }
  if (activeIdps.has('csv')) {
    numUnmanagedStudents -= upload
  }
  if (activeIdps.has('class-link')) {
    numUnmanagedStudents -= classlink
  }
  if (activeIdps.has('renaissance')) {
    numUnmanagedStudents -= renaissance
  }

  return {numUnmanagedStudents, identityProviders: Array.from(activeIdps), schoolIds}
}
